import { ReactNode, useState } from 'react'
import { Tooltip } from 'shared/components/tooltip'
import { ColumnDefinitionInterface, Table, TableProps } from './table'

interface BaseActionInterface<T> {
  shouldRender?: (instance: T) => boolean
  onChange: (instance: T) => Promise<void>
}

interface CustomInterface<T> extends Pick<BaseActionInterface<T>, 'shouldRender'> {
  tooltipCaption: string
  onActionRender?: (opened: boolean, onClose: () => void, instance: T, key: string) => JSX.Element
  onActionClick?: (instance: T) => void
  onIconRender: () => JSX.Element
  name: string
}

type CustomStateType<T> = {
  [key: CustomInterface<T>['name']]: boolean
}

interface ActionsInterface<T> {
  custom?: CustomInterface<T>[]
}

interface TableActionsProps<T> extends TableProps<T> {
  actions: ActionsInterface<T>
  testAttributePostfix?: string
}

interface ActionsRenderProps<T> {
  actions: ActionsInterface<T>
  instance: T
}

function ActionsRender<T>({ actions, instance }: ActionsRenderProps<T>): ReactNode {
  const presentRenderActions: JSX.Element[] = []
  const emptyRenderActions: JSX.Element[] = []

  const [isCustomOpen, setIsCustomOpen] = useState<CustomStateType<T>>(() => {
    const obj: CustomStateType<T> = {} as CustomStateType<T>
    actions.custom?.forEach(value => {
      obj[value.name] = false
    })
    return obj
  })

  if (actions.custom) {
    actions.custom.forEach(el => {
      const shouldRender = el.shouldRender ? el.shouldRender(instance) : true

      if (shouldRender) {
        presentRenderActions.push(
          <Tooltip label={el.tooltipCaption} mode="hover" key={`${el.name}-tooltip`}>
            <button
              type="button"
              onClick={() => {
                setIsCustomOpen(prev => ({ ...prev, [el.name]: true }))
                if (el.onActionClick) {
                  el.onActionClick(instance)
                }
              }}
              className="rounded-xs ring-brand-primary ring-offset-2 outline-hidden focus-visible:ring-2"
            >
              {el.onIconRender()}
            </button>
          </Tooltip>,
        )

        if (el.onActionRender) {
          presentRenderActions.push(
            el.onActionRender(
              isCustomOpen[el.name],
              () => setIsCustomOpen(prev => ({ ...prev, [el.name]: false })),
              instance,
              `${el.name}-modal`,
            ),
          )
        }
      } else {
        emptyRenderActions.push(<div key={`${el.name}-box`} className="size-4 sm:size-5"></div>)
      }
    })
  }

  return (
    <div className="flex items-center gap-[15px]">
      {/* eslint-disable-next-line unicorn/prefer-spread */}
      {presentRenderActions.concat(emptyRenderActions)}
    </div>
  )
}

export function TableActions<T extends { id: number }>({
  data,
  columns,
  actions,
  limiter,
  paginator,
  testAttributePostfix,
}: TableActionsProps<T>): ReactNode {
  const actionsCount = Object.keys(actions).reduce(
    (prev, cur) => {
      const key = cur as keyof ActionsInterface<T>
      return (
        prev +
        (actions[key] !== undefined
          ? Array.isArray(actions[key])
            ? (actions[key] as []).length
            : 1
          : 0)
      )
    },

    0,
  )
  const actionColumn: ColumnDefinitionInterface<T> = {
    key: 'action' as keyof T,
    header: '',
    onRender: instance => <ActionsRender actions={actions} instance={instance} />,
    actionsCount: actionsCount,
  }
  const columnsWithActions = [...columns, actionColumn]
  return (
    <Table
      data={data}
      columns={columnsWithActions}
      paginator={paginator}
      limiter={limiter}
      testAttributePostfix={testAttributePostfix}
    />
  )
}
